// @ts-check

/** Production Worker API used by the deployed admin SPA. */
export const PRODUCTION_API_BASE = 'https://puzzle-api.samliew.net';

/**
 * Returns true when a URL points at a local development host.
 *
 * @param {string|undefined|null} url
 * @returns {boolean}
 */
export function isLocalApiUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return hostname === 'localhost' || hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

/**
 * Core admin API base resolution (testable without Vite env / DOM).
 *
 * Vite bakes `VITE_API_BASE_URL` at build time (often `http://localhost:8787` from
 * `admin/.env`). Deployed hosts must never keep that localhost value — browsers
 * block cross-origin calls to localhost (CORS) and the request never reaches production.
 *
 * @param {string|undefined|null} envUrl - Value from `import.meta.env.VITE_API_BASE_URL`
 * @param {string} [hostname] - `window.location.hostname` when available
 * @returns {string} Fully qualified API base URL
 */
export function resolveAdminApiBaseUrlFrom(envUrl, hostname = '') {
  const onLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (onLocalHost) {
    return (envUrl && String(envUrl)) || PRODUCTION_API_BASE;
  }
  if (hostname) {
    // Production / preview admin hosts: ignore baked-in localhost env URLs.
    if (envUrl && !isLocalApiUrl(envUrl)) {
      return String(envUrl).replace(/\/$/, '');
    }
    return PRODUCTION_API_BASE;
  }
  // No browser hostname (tests / SSR): never return a localhost bake-in.
  if (envUrl && !isLocalApiUrl(envUrl)) {
    return String(envUrl).replace(/\/$/, '');
  }
  return PRODUCTION_API_BASE;
}

/**
 * Resolves the backend API base URL for the admin SPA.
 *
 * @returns {string} Fully qualified API base URL.
 */
export function resolveAdminApiBaseUrl() {
  // @ts-ignore
  const envUrl = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env.VITE_API_BASE_URL : undefined;
  const hostname =
    typeof window !== 'undefined' && window.location ? window.location.hostname : '';
  return resolveAdminApiBaseUrlFrom(envUrl, hostname);
}

/**
 * Admin API base URL endpoint
 */
export const API_BASE = resolveAdminApiBaseUrl();

/**
 * Points a stored puzzle image URL at the API origin.
 *
 * Discord rooms persist `/api/images/:id/file`. The admin dashboard is a
 * separate host with SPA fallback, so a root-relative `src` is served as
 * `index.html` instead of the image bytes.
 *
 * @param {string|null|undefined} url
 * @param {string} [apiBase]
 * @returns {string}
 */
export function resolveAdminMediaUrl(url, apiBase = API_BASE) {
  const raw = String(url || '').trim();
  if (!raw || raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  if (/^(?:javascript|vbscript):/i.test(raw)) return '';

  const apiMatch = raw.match(/\/api\/images\/([^/?#]+)\/(file|thumb)\/?/i);
  if (apiMatch) {
    const path = `/api/images/${apiMatch[1]}/${apiMatch[2].toLowerCase()}`;
    const base = String(apiBase || '').replace(/\/$/, '');
    return base ? `${base}${path}` : path;
  }

  if (raw.startsWith('/api/')) {
    const base = String(apiBase || '').replace(/\/$/, '');
    return base ? `${base}${raw}` : raw;
  }

  return raw;
}

/**
 * Default fallback admin secret key for local development only.
 * Production admin hosts should not prefill this value.
 */
export const DEFAULT_ADMIN_SECRET = 'puzzle_admin_secret_2026';

/**
 * Resolves the initial admin token input value.
 * Prefills the insecure default only on localhost.
 *
 * @returns {string}
 */
export function resolveInitialAdminToken() {
  if (typeof localStorage !== 'undefined') {
    try {
      const stored = localStorage.getItem('puzzle_admin_key');
      if (stored) return stored;
    } catch {}
  }
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return DEFAULT_ADMIN_SECRET;
    }
  }
  return '';
}
